import { json, postBiz, rows } from '../core/coreapi';
import {
  BudgetArbitrationRow,
  BudgetConsolidationSourceCode,
  BudgetConsolidationWorkbenchModel,
} from './budgetconsolidation.models';

import {
  normalizeAccountRow,
  normalizeArbitrationRow,
  normalizeCards,
  normalizeControlArea,
  normalizeCostCenterRow,
  normalizeWorkbench,
} from './budgetconsolidation.normalizers';

export const fetchBudgetConsolidationWorkbench = async (
  planningcycleid: number
): Promise<BudgetConsolidationWorkbenchModel> => {
  const response = await postBiz({
    s: 'budgetconsolidation',
    a: 'workbench',
    pci: planningcycleid,
  });

  return normalizeWorkbench(json(response));
};


// Compatibility reads while the server still exposes separate endpoints.

export const fetchBudgetConsolidationCards = async (
  planningcycleid: number
) =>
  normalizeCards(
    json(
      await postBiz({
        s: 'budgetconsolidation',
        a: 'cards',
        pci: planningcycleid,
      })
    )
  );

export const fetchBudgetConsolidationAccount = async (
  planningcycleid: number
) =>
  rows(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'account',
      pci: planningcycleid,
    })
  ).map(normalizeAccountRow);

export const fetchBudgetConsolidationCostCenter = async (
  planningcycleid: number
) =>
  rows(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'costcenter',
      pci: planningcycleid,
    })
  ).map(normalizeCostCenterRow);

export const fetchBudgetControlAreas = async (
  planningcycleid: number
) =>
  rows(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'controlareas',
      pci: planningcycleid,
    })
  ).map(normalizeControlArea);

export const fetchBudgetArbitrations = async (
  planningcycleid: number
) =>
  rows(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'arbitration',
      pci: planningcycleid,
    })
  ).map(normalizeArbitrationRow);


export const saveBudgetArbitrations = async (
  planningcycleid: number,
  records: BudgetArbitrationRow[]
) =>
  json(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'arbitrationsave',
      pci: planningcycleid,

      rcd: records.filter(
        (row) =>
          row.__dirty ||
          row._state === 'dirty' ||
          row._state === 'new'
      ),
    })
  );


/*
 * Consolidation owns COMPLETE only.
 *
 * Source correction is handled by returnBudgetConsolidationSource().
 */
export const transitionBudgetConsolidation = async (
  planningcycleid: number,
  stamp?: string
) =>
  json(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'transition',
      pci: planningcycleid,
      act: 'COMPLETE',
      stp: stamp ?? null,
    })
  );


/*
 * PG-01D targeted upstream return.
 */
export const returnBudgetConsolidationSource = async (
  planningcycleid: number,
  sourcecode: BudgetConsolidationSourceCode | string,
  reason: string,
  stamp?: string
) =>
  json(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'returnsource',

      pci: planningcycleid,
      src: sourcecode,
      rsn: reason,
      stp: stamp ?? null,
    })
  );


/* Add to budgetconsolidation.apis.ts.
 * Confirm the Express transition dispatcher forwards `rsn` to the SQL
 * five-argument transition function as p_reason.
 */
export const reopenBudgetConsolidation = async (
  planningcycleid: number,
  stamp: string,
  reason: string
) =>
  json(
    await postBiz({
      s: 'budgetconsolidation',
      a: 'transition',
      pci: planningcycleid,
      act: 'REOPEN',
      stp: stamp,
      rsn: reason,
    })
  );
